/**
 * Subscription Plan Management Service (Admin)
 *
 * Provides methods for system administrators to manage subscription plans
 */

import api from '../api';
import type { SubscriptionPlan } from '../../types/subscription';

export interface SubscriptionPlanWithCount extends SubscriptionPlan {
  accounts_count?: number;
}

export interface CreatePlanData {
  name: string;
  slug?: string;
  description?: string;
  price: number;
  billing_period: 'monthly' | 'yearly';
  is_active?: boolean;
  sort_order?: number;
  features?: string[];
  domains_per_campaign?: number;
  limits: {
    max_campaigns: number;
    max_team_members: number;
    max_requests_per_month: number;
    data_retention_days: number;
  };
}

export interface UpdatePlanData extends Partial<CreatePlanData> {}

export interface PlansResponse {
  plans: SubscriptionPlanWithCount[];
}

export interface PlanResponse {
  plan: SubscriptionPlanWithCount;
  message?: string;
}

export interface DeleteResponse {
  message: string;
  deleted?: boolean;
  deactivated?: boolean;
}

/**
 * Get all subscription plans (including inactive)
 */
export async function getAllPlans(isActive?: boolean): Promise<PlansResponse> {
  const response = await api.get<PlansResponse>('/api/admin/subscription-plans', {
    params: { is_active: isActive },
  });
  return response.data;
}

/**
 * Get single plan by ID
 */
export async function getPlanById(id: number): Promise<SubscriptionPlanWithCount> {
  const response = await api.get<PlanResponse>(`/api/admin/subscription-plans/${id}`);
  return response.data.plan;
}

/**
 * Create new subscription plan
 */
export async function createPlan(data: CreatePlanData): Promise<PlanResponse> {
  const response = await api.post<PlanResponse>('/api/admin/subscription-plans', data);
  return response.data;
}

/**
 * Update subscription plan
 */
export async function updatePlan(id: number, data: UpdatePlanData): Promise<PlanResponse> {
  const response = await api.put<PlanResponse>(`/api/admin/subscription-plans/${id}`, data);
  return response.data;
}

/**
 * Delete subscription plan
 */
export async function deletePlan(id: number): Promise<DeleteResponse> {
  const response = await api.delete<DeleteResponse>(`/api/admin/subscription-plans/${id}`);
  return response.data;
}

/**
 * Toggle plan active status
 */
export async function togglePlanStatus(id: number): Promise<PlanResponse> {
  const response = await api.post<PlanResponse>(`/api/admin/subscription-plans/${id}/toggle-status`);
  return response.data;
}

/**
 * Subscription plan management service object
 */
export const subscriptionPlanManagementService = {
  getAllPlans,
  getPlanById,
  createPlan,
  updatePlan,
  deletePlan,
  togglePlanStatus,
};
